<template>
    <v-col class="my-0 py-0 px-0 px-md-8 pb-15">
        <v-col class="text-start mt-5 pb-0">
            <v-alert text dense color="#8061c2" border="left" class="pl-5">
                <h1 class="title mt-1">Ver Harmonys</h1>
                <p class="desc mt-0 mb-1">Selecciona los Harmonys que deseas mostrar en la sección "Ver Harmonys".</p>
                <v-chip class="mt-2 mr-2" color="#8061c2" label outlined>
                    Total: &nbsp; <strong>{{ formattedTotalFiltered }}</strong>
                </v-chip>
                <v-chip class="mt-2 mr-2" color="#00936a" label outlined>
                    Seleccionados: &nbsp; <strong>{{ formattedTotalSelected }}</strong>
                </v-chip>
                <v-chip class="mt-2 mr-2" color="#ed6f33" label outlined>
                    Disponibles: &nbsp; <strong>{{ formattedTotalAvailable }}</strong>
                </v-chip>
            </v-alert>
        </v-col>

        <!-- Filtros -->
        <v-row class="mx-3">
            <v-col cols="12" md="3" class="px-0">
                <v-text-field
                    v-model="search"
                    @input="onSearchInput"
                    color="#8061c2"
                    label="Buscar"
                    append-icon="mdi-magnify"
                    single-line
                    hide-details
                    outlined
                    class="mb-2 mx-0 px-0"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="2" class="px-md-2 px-0">
                <v-select
                    v-model="featuredFilter"
                    :items="featuredOptions"
                    label="Mostrar"
                    outlined
                    hide-details
                ></v-select>
            </v-col>
            <v-col cols="12" md="2" class="px-md-2 px-0">
                <v-select
                    v-model="filter"
                    :items="filterOptions"
                    label="Filtrar por"
                    outlined
                    hide-details
                ></v-select>
            </v-col>
            <v-col cols="12" md="2" class="pr-md-2 px-0">
                <v-select
                    v-model="filterCreatorType"
                    :items="filterCreator"
                    label="Tipo creador"
                    outlined
                    hide-details
                ></v-select>
            </v-col>
            <v-col cols="12" md="3" class="px-0">
                <v-select
                    v-model="order"
                    :items="orderOptions"
                    label="Ordenar"
                    outlined
                    hide-details
                ></v-select>
            </v-col>
        </v-row>

        <!-- Tabla de datos -->
        <v-data-table
            :headers="headers"
            :items="harmonys"
            :loading="loading"
            class="elevation-1 mx-3 px-2 py-4"
            :items-per-page="perPage"
            :server-items-length="totalFiltered"
            :page.sync="page"
            :footer-props="{
                itemsPerPageOptions: [50, 100, 200, 300, -1],
                showFirstLastPage: true
            }"
            @update:page="fetchHarmonys"
            @update:items-per-page="changePerPage"
        >
            <template v-slot:item.logo_path="{ item }">
                <v-avatar size="35">
                    <v-img :src="item.logo_path" v-if="item.logo_path"></v-img>
                    <v-img :src="require('../../assets/illustrations/no-photo.jpg')" v-else></v-img>
                </v-avatar>
            </template>

            <template v-slot:item.visits="{ item }">
                {{ item.abbreviated_visits || 0 }}
            </template>

            <template v-slot:item.total_earnings="{ item }">
                ${{ item.total_earnings_formatted || '0.00' }}
            </template>

            <template v-slot:item.creator_type="{ item }">
                <v-chip :color="getCreatorColor(item.creator_type && item.creator_type.id ? item.creator_type.id : 0)" dark small>
                    {{ item.creator_type && item.creator_type.id ? item.creator_type.name : 'N/A' }}
                </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status.id)" dark small>
                    {{ item.status.name }}
                </v-chip>
            </template>

            <template v-slot:item.is_featured="{ item }">
                <v-chip
                    :color="item.is_featured ? '#00936a' : '#ed6f33'"
                    label
                    outlined
                    small
                >
                    {{ item.is_featured ? 'Seleccionado' : 'Disponible' }}
                </v-chip>
            </template>

            <template v-slot:item.created_at="{ item }">
                {{ item.created_at_formatted }}
            </template>

            <template v-slot:item.actions="{ item }">
                <v-tooltip bottom v-if="!item.is_featured">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="success" v-bind="attrs" v-on="on" @click="toggleFeatured(item)">
                            <v-icon>mdi-plus-circle</v-icon>
                        </v-btn>
                    </template>
                    <span>Agregar a seleccionados</span>
                </v-tooltip>
                <v-tooltip bottom v-else>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="error" v-bind="attrs" v-on="on" @click="toggleFeatured(item)">
                            <v-icon>mdi-minus-circle</v-icon>
                        </v-btn>
                    </template>
                    <span>Quitar de seleccionados</span>
                </v-tooltip>
            </template>
        </v-data-table>
    </v-col>
</template>

<script>
import axios from '@/axios.js';

export default {
    name: 'ViewHarmonys',
    data() {
        return {
            harmonys: [],
            search: '',
            searchTimeout: null,
            loading: true,
            page: 1,
            perPage: 50,
            totalFiltered: 0,
            totalSelected: 0,
            totalAvailable: 0,
            order: 'desc',
            filter: 'created_at',
            filterCreatorType: 'all',
            featuredFilter: 'all',
            headers: [
                { text: 'Logo', value: 'logo_path', align: 'start', sortable: false },
                { text: 'Nombre', value: 'name', align: 'start' },
                { text: 'Visitas', value: 'visits', sortable: true },
                { text: 'Ganancias', value: 'total_earnings', sortable: true },
                { text: 'Tipo', value: 'creator_type', sortable: false },
                { text: 'Estado', value: 'status' },
                { text: 'Selección', value: 'is_featured', sortable: false },
                { text: 'Fecha de Creación', value: 'created_at' },
                { text: 'Acciones', value: 'actions', sortable: false }
            ],
            orderOptions: [
                { text: 'Mayor', value: 'desc' },
                { text: 'Menor', value: 'asc' }
            ],
            filterOptions: [
                { text: 'Fecha Creación', value: 'created_at' },
                { text: 'Vistas', value: 'views' },
                { text: 'Ganancias', value: 'earnings' },
                { text: 'Clicks', value: 'clicks' }
            ],
            featuredOptions: [
                { text: 'Todos', value: 'all' },
                { text: 'Seleccionados', value: 'selected' },
                { text: 'Disponibles', value: 'available' }
            ],
            filterCreator: [
                { text: 'Todos', value: 'all' },
                { text: 'Adulto', value: 'Adulto' },
                { text: 'Gaming', value: 'Gaming' },
                { text: 'Influenciador', value: 'Influenciador' },
                { text: 'Comercio', value: 'Comercio' }
            ]
        };
    },
    computed: {
        formattedTotalFiltered() {
            return Number(this.totalFiltered).toLocaleString('en-US');
        },
        formattedTotalSelected() {
            return Number(this.totalSelected).toLocaleString('en-US');
        },
        formattedTotalAvailable() {
            return Number(this.totalAvailable).toLocaleString('en-US');
        }
    },
    watch: {
        order() { this.page = 1; this.fetchHarmonys(); },
        filter() { this.page = 1; this.fetchHarmonys(); },
        filterCreatorType() { this.page = 1; this.fetchHarmonys(); },
        featuredFilter() { this.page = 1; this.fetchHarmonys(); }
    },
    created() {
        this.fetchHarmonys();
        this.fetchCounts();
    },
    methods: {
        async fetchHarmonys() {
            this.loading = true;
            try {
                const response = await axios.get('/admin/get-harmonys-view', {
                    params: {
                        page: this.page,
                        per_page: this.perPage,
                        search: this.search,
                        order: this.order,
                        filter: this.filter,
                        filterCreator: this.filterCreatorType,
                        featuredFilter: this.featuredFilter
                    }
                });
                this.harmonys = response.data.sites.data;
                this.totalFiltered = response.data.totalFiltered;
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async fetchCounts() {
            try {
                const [selectedRes, availableRes] = await Promise.all([
                    axios.get('/admin/get-harmonys-view', { params: { per_page: 1, featuredFilter: 'selected' } }),
                    axios.get('/admin/get-harmonys-view', { params: { per_page: 1, featuredFilter: 'available' } })
                ]);
                this.totalSelected = selectedRes.data.totalFiltered;
                this.totalAvailable = availableRes.data.totalFiltered;
            } catch (error) {
                console.error(error);
            }
        },
        changePerPage(newPerPage) {
            this.perPage = newPerPage;
            this.page = 1;
            this.fetchHarmonys();
        },
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.page = 1;
                this.fetchHarmonys();
            }, 500);
        },
        async toggleFeatured(item) {
            const isAdding = !item.is_featured;
            const originalFeatured = item.is_featured;

            try {
                if (isAdding) {
                    await axios.post('/admin/add-featured-harmony', { site_id: item.id });
                } else {
                    await axios.post('/admin/remove-featured-harmony', { site_id: item.id });
                }

                // Actualización optimista de contadores
                if (isAdding) {
                    this.totalSelected++;
                    this.totalAvailable--;
                } else {
                    this.totalSelected--;
                    this.totalAvailable++;
                }

                // Si el filtro es 'all', solo actualizamos el item localmente sin recargar la tabla
                if (this.featuredFilter === 'all') {
                    item.is_featured = isAdding;
                } else {
                    // En filtros 'selected' o 'available', el item cambia de visibilidad
                    this.fetchHarmonys();
                }

                this.$vs.notify({
                    title: 'Éxito',
                    text: isAdding ? 'Harmony agregado a seleccionados.' : 'Harmony removido de seleccionados.',
                    color: 'success',
                    icon: 'check_box',
                    position: 'bottom-center',
                    time: 3000
                });
            } catch (error) {
                // Revertir contadores en caso de error
                if (isAdding) {
                    this.totalSelected--;
                    this.totalAvailable++;
                } else {
                    this.totalSelected++;
                    this.totalAvailable--;
                }

                if (this.featuredFilter === 'all') {
                    item.is_featured = originalFeatured;
                }

                this.$vs.notify({
                    title: 'Error',
                    text: 'No se pudo actualizar el Harmony.',
                    color: 'danger',
                    icon: 'warning',
                    position: 'bottom-center',
                    time: 3000
                });
            }
        },
        getCreatorColor(creator_id) {
            if (creator_id === 1) return '#ed6f33';
            if (creator_id === 2) return '#62a1e1';
            if (creator_id === 3) return '#e162ba';
            if (creator_id === 4) return '#00936a';
            return 'grey';
        },
        getStatusColor(statusId) {
            switch (statusId) {
                case 1: return 'orange';
                case 2: return 'gray';
                case 3: return 'rgb(101 201 105)';
                case 4: return '#ff756a';
                default: return 'blue';
            }
        }
    }
};
</script>

<style scoped>
.title {
    font-size: 22px !important;
    color: #8061c2;
}
.desc {
    color: #737373;
    font-size: 15px;
}
</style>
